import React, { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { ExpenseAttachment, PayoutLine } from '@/store/reducers/payout';
import Attachments, { PaperclipIcon } from '@/components/payout/Attachments';
import AttachmentViewer from '@/components/payout/AttachmentViewer';
import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import { formatMoney } from '@/utils/money';

interface Props {
  line: PayoutLine;
  isAdmin: boolean;
  onDefer: (line: PayoutLine) => void;
  onExclude: (line: PayoutLine) => void;
  onRestore: (line: PayoutLine) => void;
  onStopRecurring: (line: PayoutLine) => void;
  onDelete: (line: PayoutLine) => void;
  // Left out where proof cannot be added from; viewing never needs a handler.
  onAttach?: (line: PayoutLine, files: File[]) => unknown;
  onRemoveAttachment?: (attachment: ExpenseAttachment) => unknown;
}

const asMonth = (period?: string) => (
  period ? moment(period, 'YYYY-MM').format('MMM YYYY') : ''
);

/**
 * One line of a payout. An excluded or deferred line is never hidden -- the
 * employee has to be able to see that something they added is not being paid,
 * and why.
 */
const ExpenseLine: React.FC<Props> = ({
  line, isAdmin, onDefer, onExclude, onRestore, onStopRecurring, onDelete,
  onAttach, onRemoveAttachment,
}) => {
  const [{ payout, common }] = useLanguage();
  const [isOpen, toggleIsOpen] = useToggle();
  const [viewerAt, setViewerAt] = useState<number | null>(null);

  const isSettled = line.state !== 'active';
  // A copy stored before attachments existed has no such field.
  const attachments = line.attachments || [];

  return (
    <Wrapper>
      <Row onClick={toggleIsOpen}>
        <Left>
          <Title isSettled={isSettled}>{line.title}</Title>
          <Marks>
            {attachments.length > 0 && (
              // Straight to the proof from the list, without opening the line.
              <ClipMark
                accent
                title={payout.proof}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewerAt(0);
                }}
              >
                <PaperclipIcon size={11} />
                {attachments.length}
              </ClipMark>
            )}
            {line.isRecurring && <Mark>{payout.repeats_monthly}</Mark>}
            {isAdmin && line.origin === 'employee' && (
              <Mark accent>{payout.added_by_employee}</Mark>
            )}
            {line.state === 'excluded' && (
              <Mark warn>{payout.excluded_by_admin}</Mark>
            )}
            {line.state === 'deferred' && (
              <Mark warn>{`${payout.moved_to} ${asMonth(line.deferredTo)}`}</Mark>
            )}
            {line.state === 'active' && line.deferredFrom && (
              <Mark>{`${payout.moved_from} ${asMonth(line.deferredFrom)}`}</Mark>
            )}
          </Marks>
        </Left>
        <Amount isSettled={isSettled}>{formatMoney(line.amount, line.currency)}</Amount>
      </Row>

      {isOpen && (
        <Attachments
          attachments={attachments}
          onOpen={setViewerAt}
          onAdd={onAttach ? (files) => onAttach(line, files) : undefined}
          onRemove={onRemoveAttachment}
        />
      )}

      {viewerAt !== null && attachments.length > 0 && (
        <AttachmentViewer
          attachments={attachments}
          startAt={viewerAt}
          onClose={() => setViewerAt(null)}
        />
      )}

      {isOpen && (
        <Actions>
          {isAdmin && line.state === 'active' && (
            <>
              <Action onClick={() => onDefer(line)}>{payout.defer}</Action>
              <Action onClick={() => onExclude(line)}>{payout.exclude}</Action>
            </>
          )}
          {isAdmin && isSettled && line.expenseId && (
            <Action onClick={() => onRestore(line)}>{payout.restore}</Action>
          )}
          {isAdmin && line.isRecurring && line.recurringId && (
            <Action onClick={() => onStopRecurring(line)}>
              {payout.stop_recurring}
            </Action>
          )}
          {line.canDelete && (
            <Action warn onClick={() => onDelete(line)}>{common.delete}</Action>
          )}
        </Actions>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  border-bottom: solid 1px #eceff2;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  cursor: pointer;
  user-select: none;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Title = styled.div<{ isSettled: boolean }>`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ isSettled }) => (isSettled ? '#909599' : '#21272e')};
  text-decoration: ${({ isSettled }) => (isSettled ? 'line-through' : 'none')};
  word-break: break-word;
`;

const Marks = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const Mark = styled.span<{ accent?: boolean, warn?: boolean }>`
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 6px;
  margin: 2px 6px 0 0;
  border-radius: 4px;
  white-space: nowrap;
  color: ${({ accent, warn }) => {
    if (warn) return '#a35b00';
    if (accent) return '#1c5c8a';
    return '#787c80';
  }};
  background: ${({ accent, warn }) => {
    if (warn) return '#fff2dd';
    if (accent) return '#e3eef8';
    return '#f0f1f2';
  }};
`;

const ClipMark = styled(Mark)`
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  svg {
    margin-right: 3px;
  }
`;

const Amount = styled.div<{ isSettled: boolean }>`
  font-size: 14px;
  font-weight: 700;
  margin-left: 12px;
  white-space: nowrap;
  color: ${({ isSettled }) => (isSettled ? '#909599' : '#21272e')};
  text-decoration: ${({ isSettled }) => (isSettled ? 'line-through' : 'none')};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 0 0 12px;
`;

const Action = styled.div<{ warn?: boolean }>`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${({ warn }) => (warn ? '#d9534f' : '#fb7e14')};
  cursor: pointer;
  margin: 4px 16px 0 0;
`;

export default ExpenseLine;
